import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBModal from "../../component/global/DBModal";
import DBSelect from "../../component/global/DBSelect";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SETTING_ITEMS_LIST,
    SETTING_ITEMS_TYPE_TREE,
    SURVEY_LIST,
} from "../../helperData/ApiAddress";
import { flattenItemTypeOptions } from "../../pages/surveys/constants";
import { CONTENT_SOURCE, CONTENT_TARGET } from "./constants";
import {
    contentToDraft,
    detectSourceFromUrl,
    draftIssues,
    emptyDraft,
    inferTypeFromFile,
    toSelectOptions,
    validateFile,
} from "./helpers";
import MediaPreview from "./MediaPreview";

export default function ContentEditor({
    show,
    content,
    saving,
    uploadProgress,
    error,
    onHide,
    onSave,
}) {
    const { t } = useTranslation();
    const isEdit = Boolean(content?.id);
    const editedId = content?.id || 0;
    const [draft, setDraft] = useState(emptyDraft());
    const [issues, setIssues] = useState({});
    const [filePreview, setFilePreview] = useState("");
    const [itemTypeOptions, setItemTypeOptions] = useState([]);
    const [items, setItems] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const titleId = useId();
    const titleRef = useRef(null);
    const fileInputId = useId();
    const [getItemTypes] = useRequest(SETTING_ITEMS_TYPE_TREE, "GET");
    const [getItems] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [getSurveys] = useRequest(SURVEY_LIST, "GET");

    const update = (patch) => {
        setIssues({});
        setDraft((current) => ({ ...current, ...patch }));
    };

    useEffect(() => {
        if (!show) return undefined;
        setDraft(content ? contentToDraft(content) : emptyDraft());
        setIssues({});
        setFilePreview("");
        getItemTypes()
            .then((tree) => setItemTypeOptions(flattenItemTypeOptions(tree)))
            .catch(() => setItemTypeOptions([]));
        getSurveys({ page_size: 100 })
            .then((rows) => setSurveys(toSelectOptions(rows, "title")))
            .catch(() => setSurveys([]));
        return undefined;
    }, [show, editedId]);

    useEffect(() => {
        if (!show || !draft.itemTypeId) {
            setItems([]);
            return;
        }
        getItems({ item_type: draft.itemTypeId })
            .then((rows) => setItems(toSelectOptions(rows)))
            .catch(() => setItems([]));
    }, [show, draft.itemTypeId]);

    useEffect(() => {
        if (!draft.file) {
            setFilePreview("");
            return undefined;
        }
        const url = URL.createObjectURL(draft.file);
        setFilePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [draft.file]);

    const sourceOptions = useMemo(
        () =>
            Object.values(CONTENT_SOURCE).map((value) => ({
                value,
                label: t(`contentStudio.source.${value}`),
            })),
        [t]
    );
    const targetOptions = useMemo(
        () =>
            Object.values(CONTENT_TARGET).map((value) => ({
                value,
                label: t(`contentStudio.target.${value}`),
            })),
        [t]
    );

    const handleFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const fileError = validateFile(file, t);
        if (fileError) {
            setIssues({ file: fileError });
            return;
        }
        update({
            file,
            fileName: file.name,
            contentType: inferTypeFromFile(file),
            source: CONTENT_SOURCE.FILE,
            url: "",
        });
    };

    const handleUrl = (value) => {
        const detected = detectSourceFromUrl(value);
        update({
            url: value,
            source:
                detected === CONTENT_SOURCE.FILE ? draft.source : detected,
        });
    };

    const submit = (intent) => {
        const nextIssues = draftIssues(draft, t, { isEdit });
        setIssues(nextIssues);
        if (Object.keys(nextIssues).length) {
            titleRef.current?.focus();
            return;
        }
        onSave(draft, intent);
    };

    return (
        <DBModal
            show={show}
            onHide={onHide}
            minWidth={840}
            className="studio-editor"
            ariaLabelledby={titleId}
            initialFocusRef={titleRef}
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    <span id={titleId}>
                        {t(
                            isEdit
                                ? "contentStudio.editor.editTitle"
                                : "contentStudio.editor.title"
                        )}
                    </span>
                }
            />
            <DBModal.Content>
                <p className="studio-editor-lead">
                    {t("contentStudio.editor.lead")}
                </p>
                {error ? (
                    <div className="studio-alert" role="alert">
                        {error}
                    </div>
                ) : null}
                <div className="studio-editor-grid">
                    <div className="studio-editor-form">
                        <DBFieldBlock
                            title={t("contentStudio.fields.source")}
                            hint={t("contentStudio.hints.source")}
                        >
                            <div className="studio-choice-row" role="radiogroup">
                                {sourceOptions.map((option) => (
                                    <label key={option.value}>
                                        <input
                                            type="radio"
                                            name="content-source"
                                            value={option.value}
                                            checked={draft.source === option.value}
                                            onChange={() =>
                                                update({
                                                    source: option.value,
                                                    file:
                                                        option.value ===
                                                        CONTENT_SOURCE.FILE
                                                            ? draft.file
                                                            : null,
                                                })
                                            }
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </DBFieldBlock>
                        {draft.source === CONTENT_SOURCE.FILE ? (
                            <DBFieldBlock
                                title={t("contentStudio.fields.file")}
                                required
                                error={issues.file}
                            >
                                <label
                                    className="studio-file-drop"
                                    htmlFor={fileInputId}
                                >
                                    <input
                                        id={fileInputId}
                                        type="file"
                                        accept="audio/*,video/*,image/*"
                                        onChange={handleFile}
                                    />
                                    <span>
                                        {draft.fileName ||
                                            t("contentStudio.fields.fileHelp")}
                                    </span>
                                </label>
                            </DBFieldBlock>
                        ) : (
                            <DBFieldBlock
                                title={t("contentStudio.fields.url")}
                                required
                                error={issues.url}
                                hint={t(`contentStudio.hints.${draft.source}`)}
                            >
                                <DBInput
                                    value={draft.url}
                                    dir="ltr"
                                    placeholder="https://"
                                    onChange={(event) =>
                                        handleUrl(event.target.value)
                                    }
                                    aria-invalid={Boolean(issues.url)}
                                />
                            </DBFieldBlock>
                        )}
                        <DBFieldBlock
                            title={t("contentStudio.fields.target")}
                            hint={t("contentStudio.hints.target")}
                        >
                            <DBSelect
                                value={draft.targetScope}
                                options={targetOptions}
                                onChange={(value) =>
                                    update({
                                        targetScope: value,
                                        surveyId: null,
                                        itemTypeId: null,
                                        itemId: null,
                                    })
                                }
                            />
                        </DBFieldBlock>
                        {draft.targetScope === CONTENT_TARGET.SURVEY ? (
                            <DBFieldBlock
                                title={t("contentStudio.fields.survey")}
                                required
                                error={issues.surveyId}
                            >
                                <DBSelect
                                    showSearch
                                    optionFilterProp="label"
                                    value={draft.surveyId}
                                    options={surveys}
                                    onChange={(value) =>
                                        update({ surveyId: value })
                                    }
                                />
                            </DBFieldBlock>
                        ) : null}
                        {draft.targetScope === CONTENT_TARGET.ITEM_TYPE ||
                        draft.targetScope === CONTENT_TARGET.ITEM ? (
                            <DBFieldBlock
                                title={t("contentStudio.fields.itemType")}
                                required={
                                    draft.targetScope ===
                                    CONTENT_TARGET.ITEM_TYPE
                                }
                                error={issues.itemTypeId}
                            >
                                <DBSelect
                                    showSearch
                                    optionFilterProp="label"
                                    value={draft.itemTypeId}
                                    options={itemTypeOptions}
                                    onChange={(value) =>
                                        update({
                                            itemTypeId: value,
                                            itemId: null,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                        ) : null}
                        {draft.targetScope === CONTENT_TARGET.ITEM ? (
                            <DBFieldBlock
                                title={t("contentStudio.fields.item")}
                                required
                                error={issues.itemId}
                            >
                                <DBSelect
                                    showSearch
                                    optionFilterProp="label"
                                    value={draft.itemId}
                                    options={items}
                                    onChange={(value) =>
                                        update({ itemId: value })
                                    }
                                />
                            </DBFieldBlock>
                        ) : null}
                        <DBFieldBlock
                            title={t("contentStudio.fields.title")}
                            required
                            error={issues.name}
                        >
                            <DBInput
                                inputRef={titleRef}
                                value={draft.name}
                                onChange={(event) =>
                                    update({ name: event.target.value })
                                }
                                aria-invalid={Boolean(issues.name)}
                            />
                        </DBFieldBlock>
                        <DBFieldBlock
                            title={t("contentStudio.fields.description")}
                        >
                            <DBInput
                                value={draft.description}
                                onChange={(event) =>
                                    update({ description: event.target.value })
                                }
                            />
                        </DBFieldBlock>
                    </div>
                    <aside className="studio-editor-preview">
                        <p>{t("contentStudio.preview.title")}</p>
                        <MediaPreview
                            content={content}
                            draft={draft}
                            filePreview={filePreview}
                        />
                    </aside>
                </div>
                {saving && uploadProgress > 0 ? (
                    <div
                        className="studio-upload-progress"
                        role="progressbar"
                        aria-valuenow={uploadProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <span style={{ width: `${uploadProgress}%` }} />
                        <small>
                            {t("contentStudio.uploading", {
                                percent: uploadProgress,
                            })}
                        </small>
                    </div>
                ) : null}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    type="secondary"
                    outlined
                    caption={t("contentStudio.cancel")}
                    onClick={onHide}
                    disabled={saving}
                />
                <DBButton
                    type="secondary"
                    outlined
                    caption={t("contentStudio.saveDraft")}
                    onClick={() => submit("draft")}
                    loading={saving}
                />
                <DBButton
                    caption={t("contentStudio.submit")}
                    onClick={() => submit("review")}
                    loading={saving}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
